import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import axios from '@/utils/api';
import { API } from '@/utils/api';
import { toast } from 'sonner';

export default function PublisherEarnings() {
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEarnings();
  }, []);

  const fetchEarnings = async () => {
    try {
      const response = await axios.get(`${API}/publisher/earnings`);
      setEarnings(response.data);
    } catch (error) {
      toast.error('Failed to load earnings');
    } finally {
      setLoading(false);
    }
  };

  const getPayoutStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-accent border-foreground';
      case 'approved': return 'bg-primary/20 border-foreground';
      default: return 'bg-muted border-foreground';
    }
  };

  if (loading) {
    return (
      <DashboardLayout role="publisher">
        <div className="p-8">Loading...</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="publisher">
      <div className="p-8" data-testid="publisher-earnings-page">
        <h1 className="font-heading text-4xl font-bold mb-8">Earnings & Payouts</h1>

        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="border border-foreground p-6" data-testid="total-earnings-card">
            <div className="text-sm font-medium text-muted-foreground mb-2">Total Earnings</div>
            <div className="font-mono text-3xl font-bold">
              ₹{(earnings?.total_earnings || 0).toLocaleString('en-IN')}
            </div>
          </div>

          <div className="border border-foreground p-6" data-testid="pending-payout-card">
            <div className="text-sm font-medium text-muted-foreground mb-2">Pending Payout</div>
            <div className="font-mono text-3xl font-bold">
              ₹{(earnings?.pending_payout || 0).toLocaleString('en-IN')}
            </div>
          </div>

          <div className="border border-foreground p-6" data-testid="paid-out-card">
            <div className="text-sm font-medium text-muted-foreground mb-2">Paid Out</div>
            <div className="font-mono text-3xl font-bold">
              ₹{(earnings?.paid_out || 0).toLocaleString('en-IN')}
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-xl bg-white shadow-[0_2px_8px_rgba(0,0,0,0.08)] border border-[#E5E7EB]">
          <div className="border-b border-[#E5E7EB] bg-white px-6 py-4">
            <h2 className="text-lg font-bold text-[#111827]">Settlement History</h2>
          </div>
          <div className="p-0">
            {!earnings?.settlements || earnings.settlements.length === 0 ? (
              <div className="text-center py-12 text-[#9CA3AF]">
                <p>No settlements yet.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#E5E7EB] bg-[#F8F9FA]">
                      <th className="px-4 py-3 text-left text-[11px] font-medium uppercase tracking-wider text-[#6B7280]">Campaign ID</th>
                      <th className="px-4 py-3 text-left text-[11px] font-medium uppercase tracking-wider text-[#6B7280]">GMV</th>
                      <th className="px-4 py-3 text-left text-[11px] font-medium uppercase tracking-wider text-[#6B7280]">Rate Model</th>
                      <th className="px-4 py-3 text-left text-[11px] font-medium uppercase tracking-wider text-[#6B7280]">Amount Owed</th>
                      <th className="px-4 py-3 text-left text-[11px] font-medium uppercase tracking-wider text-[#6B7280]">Platform Fee</th>
                      <th className="px-4 py-3 text-left text-[11px] font-medium uppercase tracking-wider text-[#6B7280]">Status</th>
                      <th className="px-4 py-3 text-left text-[11px] font-medium uppercase tracking-wider text-[#6B7280]">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {earnings.settlements.map((settlement) => {
                      const dotColor = settlement.payout_status === 'paid' ? 'bg-emerald-500' : settlement.payout_status === 'approved' ? 'bg-blue-500' : 'bg-amber-400';
                      return (
                        <tr
                          key={settlement.id}
                          className="h-12 border-b border-[#F3F4F6] transition-colors hover:bg-[#F8F9FB]"
                          data-testid={`settlement-${settlement.id}`}
                        >
                          <td className="px-4 py-2 font-mono text-sm text-[#374151]">{settlement.campaign_id.slice(0, 8)}</td>
                          <td className="px-4 py-2 font-mono text-[#374151]">₹{settlement.gmv.toLocaleString('en-IN')}</td>
                          <td className="px-4 py-2 text-[#374151]">{settlement.rate_model}</td>
                          <td className="px-4 py-2 font-mono font-bold text-[#111827]">₹{settlement.amount_owed.toLocaleString('en-IN')}</td>
                          <td className="px-4 py-2 font-mono text-[#374151]">₹{settlement.platform_fee.toLocaleString('en-IN')}</td>
                          <td className="px-4 py-2">
                            <span className="inline-flex items-center gap-2 text-sm text-[#374151]">
                              <span className={`inline-block h-2 w-2 rounded-full ${dotColor}`} />
                              {settlement.payout_status.charAt(0).toUpperCase() + settlement.payout_status.slice(1)}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-sm text-[#6B7280]">
                            {new Date(settlement.created_at).toLocaleDateString()}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
